import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { Loader2, Printer, ArrowLeft } from "lucide-react";

const TYPE_LABEL = { invoice: "Invoice", receipt: "Receipt", credit_note: "Credit note" };

export default function CustomerStatement() {
  const { cid, token } = useParams();
  const isPublic = !!token;
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(isPublic ? `/public/customer-statement/${token}` : `/admin/customers/${cid}/statement`)
      .then((r) => setSt(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [cid, token, isPublic]);

  if (err) return <div className="p-10 text-center text-muted-foreground">{String(err)}</div>;
  if (!st) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const co = st.company, cu = st.customer, t = st.totals;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto p-6 sm:p-10 print:p-0">
        <div className="flex items-center justify-between mb-6 print:hidden">
          {isPublic ? <span className="text-xs text-muted-foreground" data-testid="cs-public-label">Shared statement · read-only</span>
            : <Button variant="ghost" onClick={() => navigate(-1)} data-testid="cs-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>}
          <Button onClick={() => window.print()} data-testid="cs-print-button"><Printer className="w-4 h-4 mr-1" /> Print / Save PDF</Button>
        </div>

        <div className="rounded-xl border border-border bg-card p-8 print:border-0" data-testid="cs-sheet">
          <div className="flex items-start justify-between gap-6">
            <div className="text-sm">
              <Logo size={30} />
              <p className="font-semibold mt-3">{co.legal_name || co.name}</p>
              {co.trn && <p className="text-xs text-muted-foreground">TRN {co.trn}</p>}
              <p className="text-xs text-muted-foreground">{[co.address, co.city, co.country].filter(Boolean).join(", ")}</p>
              <p className="text-xs text-muted-foreground">{[co.phone, co.email].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-2xl font-bold tracking-tight" data-testid="cs-title">STATEMENT OF ACCOUNT</p>
              <p className="text-xs text-muted-foreground mt-1">as of {st.today} · activity since {st.since}</p>
              <Badge variant={t.overdue > 0 ? "destructive" : t.open_balance > 0 ? "secondary" : "default"} className="mt-2" data-testid="cs-status-badge">
                {t.overdue > 0 ? `${money(t.overdue)} overdue` : t.open_balance > 0 ? "balance open" : "account clear"}
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 text-sm">
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground">Customer</p>
              <p className="font-medium" data-testid="cs-customer-name">{cu.name}</p>
              {cu.trn && <p className="text-xs text-muted-foreground">TRN {cu.trn}</p>}
              {cu.address && <p className="text-xs text-muted-foreground">{cu.address}</p>}
              <p className="text-xs text-muted-foreground">{[cu.contact_person, cu.phone].filter(Boolean).join(" · ")}</p>
            </div>
            <div><p className="text-xs text-muted-foreground">Open balance</p><p className="font-display text-xl font-bold" data-testid="cs-open-balance">{money(t.open_balance)}</p></div>
            <div><p className="text-xs text-muted-foreground">Credit terms</p><p className="font-medium">{cu.credit_days} days</p></div>
          </div>

          {st.open_items.length > 0 && (
            <div className="mt-8" data-testid="cs-open-items">
              <h3 className="font-display font-semibold text-sm mb-2">Open invoices</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Invoice</th><th className="text-left font-medium py-2">Date</th><th className="text-left font-medium py-2">Due</th>
                  <th className="text-right font-medium py-2">Invoice total</th><th className="text-right font-medium py-2">Balance</th></tr></thead>
                <tbody>{st.open_items.map((o) => (
                  <tr key={o.invoice_no} className="border-b border-border/40">
                    <td className="py-2 font-mono text-xs">{o.invoice_no}</td><td className="py-2">{o.date}</td>
                    <td className="py-2">{o.due_date}{o.overdue && <Badge variant="destructive" className="ml-2">{o.days_overdue}d overdue</Badge>}</td>
                    <td className="py-2 text-right text-muted-foreground">{money(o.total)}</td><td className="py-2 text-right font-medium">{money(o.balance)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}

          <div className="mt-8" data-testid="cs-activity">
            <h3 className="font-display font-semibold text-sm mb-2">Account activity</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-medium py-2">Date</th><th className="text-left font-medium py-2">Type</th><th className="text-left font-medium py-2">Reference</th>
                <th className="text-right font-medium py-2">Debit</th><th className="text-right font-medium py-2">Credit</th><th className="text-right font-medium py-2">Balance</th></tr></thead>
              <tbody>
                {st.lines.map((l) => (
                  <tr key={`${l.type}-${l.ref}-${l.date}-${l.running}`} className="border-b border-border/40">
                    <td className="py-2">{l.date}</td>
                    <td className="py-2">{TYPE_LABEL[l.type]}</td>
                    <td className="py-2 font-mono text-xs">{l.ref}{l.against ? <span className="text-muted-foreground"> → {l.against}</span> : ""}</td>
                    <td className="py-2 text-right">{l.debit ? money(l.debit) : ""}</td>
                    <td className="py-2 text-right text-primary">{l.credit ? money(l.credit) : ""}</td>
                    <td className="py-2 text-right text-muted-foreground">{money(l.running)}</td>
                  </tr>
                ))}
                {st.lines.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No activity in this period.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap justify-between gap-6 mt-6 text-sm">
            <div className="text-xs text-muted-foreground max-w-xs">
              {(co.bank_name || co.iban) && (
                <>
                  <p className="font-medium text-foreground">Pay by bank transfer</p>
                  {co.bank_name && <p>{co.bank_name}{co.bank_account_name ? ` · ${co.bank_account_name}` : ""}</p>}
                  {co.iban && <p>IBAN {co.iban}</p>}
                  <p>Please quote the invoice number as reference.</p>
                </>
              )}
            </div>
            <div className="w-72 space-y-1.5">
              <div className="flex justify-between text-muted-foreground"><span>Invoiced (period)</span><span>{money(t.invoiced_period)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Received (period)</span><span>{money(t.received_period)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Credit notes</span><span>{money(t.credited_period)}</span></div>
              <div className="flex justify-between font-display font-bold text-base border-t border-border pt-2"><span>Open balance</span><span>{money(t.open_balance)}</span></div>
              {t.overdue > 0 && <div className="flex justify-between text-destructive font-medium"><span>of which overdue</span><span>{money(t.overdue)}</span></div>}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-8">Open invoices are listed in full regardless of age; settled invoices, receipts and credit notes are shown for the last {st.period_days} days. Please report any discrepancy within 7 days.</p>
        </div>
      </div>
    </div>
  );
}
